import { Absolute, Icon, Text, Token, VStack } from '@revolut-internal/ui-kit'
import { TableColumn } from '@revolut-internal/ui-kit-table'

import { Security } from 'common/security/security'
import { BreachCell } from 'modules/Monitoring/components/TableCells/BreachCell'
import { EmptyCell } from 'modules/Monitoring/components/TableCells/EmptyCell'
import { FadedCell } from 'modules/Monitoring/components/TableCells/FadedCell'
import { TagsCell } from 'modules/Monitoring/components/TableCells/TagsCell'
import { CheckboxSelectFilter } from 'modules/Monitoring/components/TableFilters/CheckboxSelectFilter'
import { Tooltip } from 'modules/Monitoring/components/Tooltip/Tooltip'
import { Agent, AgentAttendanceLabel, AgentStatus } from 'modules/Monitoring/types'
import { enumToOptions } from 'modules/Monitoring/utils/enumToOptions'
import { formatNumber } from 'modules/Monitoring/utils/formatNumber'
import {
  convertSafeTimeToDuration,
  formatDuration,
  safeFormat,
} from 'modules/Monitoring/utils/formatTimeAndDuration'
import { getSafeDurationAccessor } from 'modules/Monitoring/utils/getSafeDurationAccessor'
import { pluralize } from 'modules/Monitoring/utils/pluralize'

import { SelectableCell } from '../../components/SelectableCell'

import { ShiftScheduleFilter } from './components/ShiftScheduleFilter'
import { StatusTag } from './components/StatusTag'
import { AgentAttendanceFormattedData, AgentAttendanceShiftState } from './types'

export type TicketSideData = { agent: Agent; shiftId: string }

const agentStatusOptions = enumToOptions(AgentStatus)
const agenAttendanceLabelOptions = enumToOptions(AgentAttendanceLabel)

export const getColumns = ({
  selectedTicketsSideData,
  selectedAdherenceShiftData,
  agentAttendanceDetails,
  entityName,
  onShowTickets,
  onShowAdherenceTimeline,
}: {
  agentAttendanceDetails: AgentAttendanceFormattedData[]
  entityName: string
  selectedTicketsSideData?: TicketSideData
  selectedAdherenceShiftData?: TicketSideData
  onShowTickets: (args?: TicketSideData) => void
  onShowAdherenceTimeline: (args?: TicketSideData) => void
}): TableColumn<AgentAttendanceFormattedData>[] => [
  {
    header: 'Agent',
    id: 'name',
    minSize: 220,
    accessorFn: ({ agent }) => agent?.name,
    cell: ({ row }) => {
      if (!row.original.subRows) {
        return <EmptyCell />
      }

      return row.original.agent ? (
        <VStack>
          {row.original.agent.name}
          <Text variant="tiny" color="grey-tone-50">
            {row.original.agent.email}
          </Text>
        </VStack>
      ) : (
        <EmptyCell />
      )
    },

    enableSorting: true,
  },
  {
    header: `Assigned ${entityName}s`,
    accessorKey: 'assignedTicketCount',
    sortUndefined: -1,
    meta: {
      volatileCell: true,
    },
    cell: ({ row, cell, getValue }) => {
      const value = getValue()

      if (!row.original.subRows) {
        return <EmptyCell />
      }

      const content = `${formatNumber(value ?? 0)} ${pluralize(entityName, value, true)}`
      const isSelected =
        selectedTicketsSideData?.agent.id === row.original.agent?.id &&
        selectedTicketsSideData?.shiftId === row.original.shiftId

      return value && row.original.agent && Security.canViewAgentTickets ? (
        <SelectableCell
          cell={cell}
          isSelected={isSelected}
          onClick={() => {
            isSelected || !row.original.agent || !row.original.shiftId
              ? onShowTickets(undefined)
              : onShowTickets({
                  agent: row.original.agent,
                  shiftId: row.original.shiftId,
                })
          }}
        >
          {content}
        </SelectableCell>
      ) : (
        content
      )
    },

    enableSorting: true,
  },
  {
    header: 'Shift schedule',
    accessorKey: 'specialisation',
    minSize: 200,
    meta: {
      filter: props => (
        <ShiftScheduleFilter
          {...props}
          agentAttendanceDetails={agentAttendanceDetails}
          label="Shift Filter"
          labelList="Select Shift"
        />
      ),
    },
    cell: ({ row, cell }) => {
      return (
        <FadedCell
          cell={cell}
          isFaded={
            !row.original.subRows &&
            row.original.currentStatus === AgentAttendanceShiftState.Past
          }
        >
          <Text style={{ position: 'relative' }} pr="s-32">
            {row.original.specialisation}
            {!row.original.productive && (
              <Absolute
                top="50%"
                left={`calc(100% - ${Token.space.s20})`}
                style={{ transform: 'translateY(-50%)' }}
              >
                <Tooltip message="Unproductive">
                  <Icon name="MinusCircle" color="grey-tone-20" size={16} />
                </Tooltip>
              </Absolute>
            )}
          </Text>
        </FadedCell>
      )
    },
    enableColumnFilter: true,

    enableSorting: true,
  },
  {
    header: 'Start',
    accessorKey: 'startDate',
    cell: ({ row, cell }) => {
      return (
        <FadedCell
          cell={cell}
          isFaded={
            !row.original.subRows &&
            row.original.currentStatus === AgentAttendanceShiftState.Past
          }
        >
          {safeFormat(row.original.startDate, 'd MMM yyyy, HH:mm')}
        </FadedCell>
      )
    },

    enableSorting: true,
  },
  {
    header: 'End',
    accessorKey: 'endDate',
    cell: ({ row, cell }) => {
      return (
        <FadedCell
          cell={cell}
          isFaded={
            !row.original.subRows &&
            row.original.currentStatus === AgentAttendanceShiftState.Past
          }
        >
          {safeFormat(row.original.endDate, 'd MMM yyyy, HH:mm')}
        </FadedCell>
      )
    },

    enableSorting: true,
  },
  {
    header: 'Status',
    accessorKey: 'currentStatus',
    sortUndefined: 'last',
    meta: {
      filter: props => (
        <CheckboxSelectFilter {...props} label="Status" options={agentStatusOptions} />
      ),
    },
    cell: ({ row }) => {
      return row.original.currentStatus ? (
        <StatusTag status={row.original.currentStatus} />
      ) : null
    },
    enableSorting: true,

    enableColumnFilter: true,
  },
  {
    header: 'Labels',
    accessorKey: 'labels',
    minSize: 200,
    filterFn: 'arrIncludesSome',
    meta: {
      filter: props => (
        <CheckboxSelectFilter
          {...props}
          label="Label"
          options={agenAttendanceLabelOptions}
        />
      ),
    },
    cell: ({ row }) => {
      return <TagsCell tags={row.original.labels} />
    },
    enableSorting: false,

    enableColumnFilter: true,
  },
  {
    header: 'Adherence, %',
    minSize: 120,
    accessorFn: ({ metrics }) =>
      metrics?.adherence ? Math.round(metrics?.adherence.value) : undefined,
    sortUndefined: -1,
    meta: {
      volatileCell: true,
    },
    cell: ({ row, cell }) => {
      const isSelected = Boolean(
        row.original.subRows &&
          selectedAdherenceShiftData?.shiftId === row.original.shiftId,
      )
      const value = row.original.metrics?.adherence
        ? `${formatNumber(Math.round(row.original.metrics?.adherence.value))}%`
        : null

      return value ? (
        <SelectableCell
          cell={cell}
          isSelected={isSelected}
          onClick={() => {
            isSelected || !row.original.shiftId || !row.original.agent
              ? onShowAdherenceTimeline(undefined)
              : onShowAdherenceTimeline({
                  shiftId: row.original.shiftId,
                  agent: row.original.agent,
                })
          }}
        >
          <BreachCell
            slaBreach={row.original.metrics?.adherence?.slaBreach}
            showIndicator={false}
          >
            {value}
          </BreachCell>
        </SelectableCell>
      ) : null
    },

    enableSorting: true,
  },
  {
    header: 'Active time',
    minSize: 150,
    sortingFn: 'basic',
    accessorFn: ({ metrics }) => getSafeDurationAccessor(metrics?.activeTime),

    cell: ({ row }) => {
      return row.original.metrics?.activeTime
        ? formatDuration(convertSafeTimeToDuration(row.original.metrics?.activeTime))
        : null
    },
    enableSorting: true,
  },
]
